/**
 * ContentList 컴포넌트
 * 다국어 지원 콘텐츠 목록 표시 컴포넌트
 *
 * 요구사항 3: 콘텐츠 목록에서 다국어 정보를 한눈에 파악하고 언어별로 콘텐츠를 확인
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Select, Table, Tag, Space, Typography, Button, Dropdown, Menu } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, FilterOutlined, SortAscendingOutlined } from '@ant-design/icons';

import {
  SUPPORTED_LANGUAGES,
  LanguageCode,
  DEFAULT_LANGUAGE,
  getLanguageInfo,
} from '../../constants/i18n/languages';
import { ContentData } from '../../types/i18n/i18nTypes';
import {
  getTranslation,
  getAvailableLanguages,
  convertLegacyToI18n,
} from '../../services/i18n/i18nService';

import './ContentList.css';

const { Option } = Select;
const { Text } = Typography;

/**
 * 필터링 옵션 타입
 */
type FilterOption = 'all' | 'translated' | 'untranslated' | 'complete';

/**
 * 정렬 옵션 타입
 */
type SortOption = 'title' | 'created' | 'translation_completeness';

/**
 * ContentList 컴포넌트 Props
 */
export interface ContentListProps {
  /** 콘텐츠 목록 데이터 */
  contents: ContentData[];
  /** 편집 핸들러 */
  onEdit?: (content: ContentData) => void;
  /** 로딩 상태 */
  loading?: boolean;
  /** 페이지네이션 설정 */
  pagination?: any;
  /** 추가 테이블 Props */
  tableProps?: any;
}

/**
 * ContentList 컴포넌트
 *
 * 기능:
 * - 언어 선택 드롭다운 (요구사항 3.1)
 * - 선택된 언어로 제목 표시 (폴백 지원) (요구사항 3.2)
 * - 각 콘텐츠의 지원 언어 칩 표시 (요구사항 3.3)
 * - 현재 선택 언어 강조 표시 (요구사항 3.4)
 * - 언어별 콘텐츠 필터링 및 정렬 기능 (요구사항 3.5, 3.6)
 */
export const ContentList: React.FC<ContentListProps> = ({
  contents,
  onEdit,
  loading = false,
  pagination,
  tableProps,
}) => {
  // 현재 선택된 언어 상태 (요구사항 3.1)
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>(DEFAULT_LANGUAGE);

  // 필터링 및 정렬 상태
  const [filterOption, setFilterOption] = useState<FilterOption>('all');
  const [sortOption, setSortOption] = useState<SortOption>('title');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  /**
   * 언어 선택 변경 핸들러 (요구사항 3.5)
   */
  const handleLanguageChange = useCallback((language: LanguageCode) => {
    setSelectedLanguage(language);
  }, []);

  /**
   * 콘텐츠의 제목을 선택된 언어로 가져오는 함수 (요구사항 3.2)
   */
  const getContentTitle = useCallback((content: ContentData, language: LanguageCode): string => {
    const formData = convertLegacyToI18n(content);

    // 선택된 언어의 제목을 가져오고, 없으면 폴백 언어 사용
    const title = getTranslation(formData.i18n_title, language);

    return title || content.title || '제목 없음';
  }, []);

  /**
   * 콘텐츠가 번역이 없는지 확인하는 함수 (요구사항 3.6)
   */
  const isTranslationMissing = useCallback(
    (content: ContentData, language: LanguageCode): boolean => {
      const formData = convertLegacyToI18n(content);
      const title = formData.i18n_title?.[language];

      // 선택된 언어의 번역이 없고, 기본 언어도 아닌 경우
      return !title && language !== DEFAULT_LANGUAGE;
    },
    []
  );

  /**
   * 콘텐츠의 지원 언어 목록을 가져오는 함수 (요구사항 3.3)
   */
  const getContentLanguages = useCallback((content: ContentData): LanguageCode[] => {
    const formData = convertLegacyToI18n(content);
    return getAvailableLanguages(formData);
  }, []);

  /**
   * 콘텐츠의 번역 완성도를 계산하는 함수
   */
  const getTranslationCompleteness = useCallback((content: ContentData): number => {
    const formData = convertLegacyToI18n(content);
    const availableLanguages = getAvailableLanguages(formData);
    const totalLanguages = Object.keys(SUPPORTED_LANGUAGES).length;
    return Math.round((availableLanguages.length / totalLanguages) * 100);
  }, []);

  /**
   * 필터링된 콘텐츠 목록을 가져오는 함수
   */
  const filteredContents = useMemo(() => {
    let filtered = [...contents];

    // 언어별 필터링
    if (filterOption !== 'all') {
      filtered = filtered.filter(content => {
        const availableLanguages = getContentLanguages(content);
        const hasSelectedLanguage = availableLanguages.includes(selectedLanguage);
        const completeness = getTranslationCompleteness(content);

        switch (filterOption) {
          case 'translated':
            return hasSelectedLanguage;
          case 'untranslated':
            return !hasSelectedLanguage;
          case 'complete':
            return completeness === 100;
          default:
            return true;
        }
      });
    }

    // 정렬
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortOption) {
        case 'title':
          const titleA = getContentTitle(a, selectedLanguage);
          const titleB = getContentTitle(b, selectedLanguage);
          comparison = titleA.localeCompare(titleB, 'ko');
          break;
        case 'created':
          comparison = new Date(a.created).getTime() - new Date(b.created).getTime();
          break;
        case 'translation_completeness':
          const completenessA = getTranslationCompleteness(a);
          const completenessB = getTranslationCompleteness(b);
          comparison = completenessA - completenessB;
          break;
        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [
    contents,
    filterOption,
    sortOption,
    sortOrder,
    selectedLanguage,
    getContentTitle,
    getContentLanguages,
    getTranslationCompleteness,
  ]);

  /**
   * 테이블 컬럼 정의
   */
  const columns: ColumnsType<ContentData> = useMemo(
    () => [
      {
        title: 'Title',
        dataIndex: 'title',
        key: 'title',
        render: (_, content) => {
          const title = getContentTitle(content, selectedLanguage);
          const isMissing = isTranslationMissing(content, selectedLanguage);

          return (
            <Space direction="vertical" size={4}>
              <Text
                className={isMissing ? 'content-list-title-missing' : 'content-list-title'}
                title={isMissing ? 'No translation' : title}
              >
                {title}
              </Text>
              {isMissing && (
                <Text type="secondary" className="content-list-fallback-notice">
                  (기본 언어로 표시)
                </Text>
              )}
            </Space>
          );
        },
      },
      {
        title: 'Supported Languages',
        key: 'languages',
        render: (_, content) => {
          const availableLanguages = getContentLanguages(content);
          const completeness = getTranslationCompleteness(content);

          return (
            <Space direction="vertical" size={4}>
              <Space wrap>
                {availableLanguages.map(language => {
                  const languageInfo = getLanguageInfo(language);
                  const isSelected = language === selectedLanguage;

                  return (
                    <Tag
                      key={language}
                      className={
                        isSelected
                          ? 'content-list-language-tag-selected'
                          : 'content-list-language-tag'
                      }
                      color={isSelected ? 'blue' : 'default'}
                    >
                      <span className="content-list-language-flag">{languageInfo.flag}</span>
                      {languageInfo.label}
                    </Tag>
                  );
                })}
                {availableLanguages.length === 0 && <Text type="secondary">지원 언어 없음</Text>}
              </Space>
              <Text type="secondary" className="content-list-completeness">
                번역 완성도: {completeness}%
              </Text>
            </Space>
          );
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => {
          const statusMap: Record<string, { color: string; text: string }> = {
            draft: { color: 'default', text: '초안' },
            published: { color: 'green', text: '게시됨' },
            archived: { color: 'red', text: '보관됨' },
          };

          const statusInfo = statusMap[status] || { color: 'default', text: status };

          return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
        },
      },
      {
        title: 'Created',
        dataIndex: 'created',
        key: 'created',
        render: (created: string) => {
          return new Date(created).toLocaleDateString('ko-KR');
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, content) => (
          <Space>
            {onEdit && (
              <a onClick={() => onEdit(content)} className="content-list-edit-link">
                <EditOutlined /> 편집
              </a>
            )}
          </Space>
        ),
      },
    ],
    [
      selectedLanguage,
      getContentTitle,
      isTranslationMissing,
      getContentLanguages,
      getTranslationCompleteness,
      onEdit,
    ]
  );

  /**
   * 필터 메뉴 렌더링
   */
  const renderFilterMenu = () => (
    <Menu selectedKeys={[filterOption]} onClick={({ key }) => setFilterOption(key as FilterOption)}>
      <Menu.Item key="all">모든 콘텐츠</Menu.Item>
      <Menu.Item key="translated">선택 언어 번역 있음</Menu.Item>
      <Menu.Item key="untranslated">선택 언어 번역 없음</Menu.Item>
      <Menu.Item key="complete">모든 언어 번역 완료</Menu.Item>
    </Menu>
  );

  /**
   * 정렬 메뉴 렌더링
   */
  const renderSortMenu = () => (
    <Menu
      selectedKeys={[`${sortOption}_${sortOrder}`]}
      onClick={({ key }) => {
        const [option, order] = key.split('_');
        setSortOption(option as SortOption);
        setSortOrder(order as 'asc' | 'desc');
      }}
    >
      <Menu.Item key="title_asc">제목 (가나다순)</Menu.Item>
      <Menu.Item key="title_desc">제목 (다나가순)</Menu.Item>
      <Menu.Item key="created_asc">생성일 (오래된순)</Menu.Item>
      <Menu.Item key="created_desc">생성일 (최신순)</Menu.Item>
      <Menu.Item key="translation_completeness_asc">번역 완성도 (낮은순)</Menu.Item>
      <Menu.Item key="translation_completeness_desc">번역 완성도 (높은순)</Menu.Item>
    </Menu>
  );

  /**
   * 언어 선택 드롭다운 렌더링 (요구사항 3.1)
   */
  const renderLanguageSelector = () => (
    <div className="content-list-language-selector">
      <Text strong>표시 언어: </Text>
      <Select
        value={selectedLanguage}
        onChange={handleLanguageChange}
        style={{ width: 150 }}
        className="content-list-language-select"
      >
        {Object.entries(SUPPORTED_LANGUAGES).map(([code, info]) => (
          <Option key={code} value={code}>
            <Space>
              <span>{info.flag}</span>
              <span>{info.label}</span>
            </Space>
          </Option>
        ))}
      </Select>
    </div>
  );

  /**
   * 필터 및 정렬 컨트롤 렌더링
   */
  const renderFilterSortControls = () => (
    <div className="content-list-controls">
      <Space>
        <Dropdown overlay={renderFilterMenu()} trigger={['click']}>
          <Button icon={<FilterOutlined />}>
            필터:{' '}
            {filterOption === 'all'
              ? '전체'
              : filterOption === 'translated'
                ? '번역됨'
                : filterOption === 'untranslated'
                  ? '번역안됨'
                  : '완료됨'}
          </Button>
        </Dropdown>
        <Dropdown overlay={renderSortMenu()} trigger={['click']}>
          <Button icon={<SortAscendingOutlined />}>
            정렬:{' '}
            {sortOption === 'title' ? '제목' : sortOption === 'created' ? '생성일' : '번역완성도'} (
            {sortOrder === 'asc' ? '오름차순' : '내림차순'})
          </Button>
        </Dropdown>
      </Space>
    </div>
  );

  return (
    <div className="content-list">
      {/* 언어 선택 드롭다운 및 필터/정렬 컨트롤 */}
      <div className="content-list-header">
        {renderLanguageSelector()}
        {renderFilterSortControls()}
      </div>

      {/* 콘텐츠 테이블 */}
      <Table
        columns={columns}
        dataSource={filteredContents}
        loading={loading}
        pagination={pagination}
        rowKey="id"
        className="content-list-table"
        {...tableProps}
      />
    </div>
  );
};

export default ContentList;
